import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { useUnpublishProductDeveloperOverwritesMutation } from '../../../../../graphql/mutation/product/productDeveloperOverwrite/unpublishProductDeveloperOverwritesMutation';
import { usePublishProductDeveloperOverwritesMutation } from '../../../../../graphql/mutation/product/productDeveloperOverwrite/publishProductDeveloperOverwritesMutation';
import useNotification from '../../../../components/_hook/useNotification';
import StatusIconButton from '../../../../components/_iconButton/StatusIconButton';
import useRouter from '../../../../components/_hook/useRouter';
import { IOverwriteMetaTagSubSchema } from '../../../../../graphql/type/subSchema/overwriteMetaTag';
import { useDeleteProductDeveloperOverwritesMutation } from '../../../../../graphql/mutation/product/productDeveloperOverwrite/deleteProductDeveloperOverwritesMutation';
import DeleteIconButton from '../../../../components/_iconButton/DeleteIconButton';
import DialogConfirm from '../../../../components/_dialog/DialogConfirm';
import FormCreateUpdateProductDeveloperOverwrite from './_module/FormCreateUpdateProductDeveloperOverwrite';
import CopyIconButton from '../../../../components/_iconButton/CopyIconButton';
import useLanguage from '../../../../components/_hook/useLanguage';

interface IProps {
  item: IOverwriteMetaTagSubSchema;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function ProductDeveloperOverwriteTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey } = props;
  const { notification } = useNotification();
  const { params } = useRouter();
  const { getText } = useLanguage();
  const [isOpenUpdateDialog, setIsOpenUpdateDialog] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const { docID: productId } = params;
  const [
    publishProductDeveloperOverwritesMutation,
    { loading: isPublishingProductDeveloperOverwrites },
  ] = usePublishProductDeveloperOverwritesMutation(
    `updatedProduct {
				_id
				developer {
                  overwrite {
                    id
                    status
                  }
                }
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [
    unpublishProductDeveloperOverwritesMutation,
    { loading: isUnpublishingProductDeveloperOverwrites },
  ] = useUnpublishProductDeveloperOverwritesMutation(
    `updatedProduct {
				_id
				developer {
                  overwrite {
                    id
                    status
                  }
                }
			}`,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  const [
    deleteProductDeveloperOverwritesMutation,
    { loading: isDeletingProductDeveloperOverwrites },
  ] = useDeleteProductDeveloperOverwritesMutation(
    `updatedProduct {
				_id
				developer {
                  overwrite {
                    id
                    status
                  }
                }
			}`,
    {
      onCompleted: () => {
        notification.success(
          t('__item___deleted', { item: `${item.id} ${t('overwrite')}` })
        );
        if (setTableRefreshKey) {
          setTableRefreshKey(new Date().getTime());
        }
      },
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  async function onClickDelete() {
    await deleteProductDeveloperOverwritesMutation({
      variables: {
        productId,
        developerOverwriteIds: [item.id],
      },
    });
  }

  async function onClickPublish() {
    await publishProductDeveloperOverwritesMutation({
      variables: {
        productId,
        developerOverwriteIds: [item.id],
      },
    });
  }

  async function onClickUnpublish() {
    await unpublishProductDeveloperOverwritesMutation({
      variables: {
        productId,
        developerOverwriteIds: [item.id],
      },
    });
  }

  return (
    <>
      <DialogConfirm
        title={t('are_you_sure_you_want_to_delete__item__', { item: item.id })}
        open={isOpenDeleteDialog}
        onClose={() => setIsOpenDeleteDialog(false)}
        onConfirm={onClickDelete}
      />
      <Dialog
        open={isOpenUpdateDialog}
        onClose={() => setIsOpenUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <FormCreateUpdateProductDeveloperOverwrite
          overwriteId={item.id}
          onSubmit={() => {
            setIsOpenUpdateDialog(false);
          }}
          onCancel={() => {
            setIsOpenUpdateDialog(false);
          }}
        />
      </Dialog>
      <TableCell align="left">
        <Tooltip title={item.id}>
          <Button color="primary" onClick={() => setIsOpenUpdateDialog(true)}>
            {item.id.substr(0, 6)}
          </Button>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item.id} />
      </TableCell>
      <TableCell align="center">{t(item.name)}</TableCell>
      <TableCell align="center">{getText(item.content)}</TableCell>
      <TableCell align="right">
        <StatusIconButton
          status={item.status}
          loading={
            isUnpublishingProductDeveloperOverwrites ||
            isPublishingProductDeveloperOverwrites
          }
          tooltip={item.status ? t('publish') : t('unpublish')}
          onClick={item.status ? onClickUnpublish : onClickPublish}
        />
        <DeleteIconButton
          loading={isDeletingProductDeveloperOverwrites}
          tooltip={t('delete')}
          onClick={() => setIsOpenDeleteDialog(true)}
        />
      </TableCell>
    </>
  );
}
